import { Box, Button, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  BarcodeFormat,
  SCAN_INTERVAL_MS,
  hasNativeBarcodeDetector,
  isCameraAvailable,
  isPermissionDeniedError,
  toNativeBarcodeFormat,
} from './camera-scanner.utils';

export interface CameraScannerProps {
  formats: BarcodeFormat[];
  onDetect: (value: string) => void;
  onClose: () => void;
}

export function isScanButtonAvailable(): boolean {
  return typeof navigator !== 'undefined' && isCameraAvailable(navigator.mediaDevices);
}

async function decodeFrame(imageData: ImageData, formats: BarcodeFormat[]): Promise<string | null> {
  if (hasNativeBarcodeDetector()) {
    const NativeBarcodeDetector = (window as unknown as { BarcodeDetector: new (options: { formats: string[] }) => { detect: (source: ImageData) => Promise<Array<{ rawValue: string }>> } }).BarcodeDetector;
    const detector = new NativeBarcodeDetector({ formats: formats.map(toNativeBarcodeFormat) });
    const results = await detector.detect(imageData);
    return results[0]?.rawValue ?? null;
  }

  const { readBarcodes } = await import('zxing-wasm/reader');
  const results = await readBarcodes(imageData, { formats });
  return results[0]?.text ?? null;
}

export function CameraScanner({ formats, onDetect, onClose }: CameraScannerProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isProcessingRef = useRef(false);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [scanFailed, setScanFailed] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  const stopStream = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function start() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }

        intervalRef.current = setInterval(async () => {
          const video = videoRef.current;
          const canvas = canvasRef.current;
          if (!video || !canvas || video.videoWidth === 0) {
            return;
          }

          if (isProcessingRef.current) {
            return;
          }

          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          const context = canvas.getContext('2d');
          if (!context) {
            return;
          }

          context.drawImage(video, 0, 0, canvas.width, canvas.height);
          const imageData = context.getImageData(0, 0, canvas.width, canvas.height);

          isProcessingRef.current = true;
          try {
            const value = await decodeFrame(imageData, formats);
            if (value) {
              stopStream();
              onDetect(value);
            }
          } catch {
            stopStream();
            setScanFailed(true);
          } finally {
            isProcessingRef.current = false;
          }
        }, SCAN_INTERVAL_MS);
      } catch (error) {
        if (isPermissionDeniedError(error)) {
          setPermissionDenied(true);
        }
      }
    }

    start();

    return () => {
      cancelled = true;
      stopStream();
    };
  }, [formats, onDetect, stopStream, retryCount]);

  if (permissionDenied) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" gap={2} p={3}>
        <Typography variant="body1" align="center">
          É necessário permitir o uso da câmera pelo navegador para usar este recurso
        </Typography>
        <Button
          variant="contained"
          onClick={() => {
            setPermissionDenied(false);
            setRetryCount((count) => count + 1);
          }}
        >
          HABILITAR CÂMERA
        </Button>
        <IconButton onClick={onClose} aria-label="fechar">
          <CloseIcon />
        </IconButton>
      </Box>
    );
  }

  if (scanFailed) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" gap={2} p={3}>
        <Typography variant="body1" align="center">
          Não conseguimos ler o código pela câmera. Cole o código no campo acima para continuar.
        </Typography>
        <IconButton onClick={onClose} aria-label="fechar">
          <CloseIcon />
        </IconButton>
      </Box>
    );
  }

  return (
    <Box position="relative" width="100%" height="100%" bgcolor="black">
      <video ref={videoRef} muted playsInline style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      <canvas ref={canvasRef} style={{ display: 'none' }} />
      <IconButton
        onClick={() => {
          stopStream();
          onClose();
        }}
        aria-label="fechar"
        sx={{ position: 'absolute', top: 8, right: 8, color: 'white' }}
      >
        <CloseIcon />
      </IconButton>
    </Box>
  );
}
